import type { UserStatus } from '@/lib/auth-client';

interface StatusBadgeProps {
  status: UserStatus;
}

const statusStyles: Record<UserStatus, { bg: string; text: string; label: string }> = {
  ACTIVE: { bg: 'bg-sage/20', text: 'text-sage', label: 'Active' },
  SUSPENDED: { bg: 'bg-gold/20', text: 'text-gold', label: 'Suspended' },
  BANNED: { bg: 'bg-red/20', text: 'text-red', label: 'Banned' }
};

export function StatusBadge({ status }: StatusBadgeProps) {
  const style = statusStyles[status];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
    >
      {style.label}
    </span>
  );
}

interface RoleBadgeProps {
  role: 'USER' | 'ADMIN';
}

export function RoleBadge({ role }: RoleBadgeProps) {
  if (role === 'ADMIN') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose/20 text-rose">
        Admin
      </span>
    );
  }
  return null;
}

interface AnonymizedBadgeProps {
  anonymizedAt?: string | null;
}

export function AnonymizedBadge({ anonymizedAt }: AnonymizedBadgeProps) {
  if (!anonymizedAt) return null;

  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-warm/50 text-sepia/70"
      title={`Anonymized on ${new Date(anonymizedAt).toLocaleDateString()}`}
    >
      Anonymized
    </span>
  );
}

interface ReportStatusBadgeProps {
  status: string;
}

export function ReportStatusBadge({ status }: ReportStatusBadgeProps) {
  const styles: Record<string, { bg: string; text: string }> = {
    PENDING: { bg: 'bg-gold/20', text: 'text-gold' },
    REVIEWED: { bg: 'bg-blue/20', text: 'text-blue' },
    RESOLVED: { bg: 'bg-sage/20', text: 'text-sage' },
    DISMISSED: { bg: 'bg-gray-warm/50', text: 'text-sepia/70' }
  };
  const style = styles[status] || styles.PENDING;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

interface LetterStatusBadgeProps {
  status: string;
  isFlagged?: boolean;
}

export function LetterStatusBadge({ status, isFlagged }: LetterStatusBadgeProps) {
  const styles: Record<string, { bg: string; text: string }> = {
    DRAFT: { bg: 'bg-gray-warm/50', text: 'text-sepia/70' },
    IN_TRANSIT: { bg: 'bg-blue/20', text: 'text-blue' },
    DELIVERED: { bg: 'bg-sage/20', text: 'text-sage' },
    READ: { bg: 'bg-rose/20', text: 'text-rose' }
  };
  const style = styles[status] || styles.DRAFT;

  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
      >
        {status.replace('_', ' ')}
      </span>
      {isFlagged && (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red/20 text-red">
          Flagged
        </span>
      )}
    </div>
  );
}
